import { DEFAULT_LEVEL, type Difficulty, isLevel, type Level, LEVELS } from '@arrow/puzzle-schema';

/**
 * Daily puzzles and the streak.
 *
 * A date key is `YYYY-MM-DD` in the player's **local** calendar. All arithmetic is done
 * on the calendar components via `Date.UTC`, so DST changes (23- or 25-hour local days)
 * can never shift a key. Never derive a key by slicing an ISO string.
 */

export type DateKey = string;

const KEY = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86_400_000;

function pad(n: number, width = 2): string {
	return String(n).padStart(width, '0');
}

/** Parses a key to its UTC-midnight epoch ms, or null if it isn't a real date. */
function keyToUtc(key: string): number | null {
	if (typeof key !== 'string') return null;
	const m = KEY.exec(key);
	if (!m) return null;
	const y = Number(m[1]);
	const mo = Number(m[2]);
	const d = Number(m[3]);
	const utc = Date.UTC(y, mo - 1, d);
	const back = new Date(utc);
	// Rejects 2026-02-30 and friends (Date.UTC would roll them over).
	if (back.getUTCFullYear() !== y || back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d) return null;
	return utc;
}

function utcToKey(utc: number): DateKey {
	const d = new Date(utc);
	return `${pad(d.getUTCFullYear(), 4)}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

function mustParse(key: string): number {
	const utc = keyToUtc(key);
	if (utc === null) throw new RangeError(`Invalid date key: ${String(key)}`);
	return utc;
}

export function isDateKey(value: unknown): value is DateKey {
	return typeof value === 'string' && keyToUtc(value) !== null;
}

/** The player's local calendar date for an instant. */
export function localDateKey(date: Date): DateKey {
	return `${pad(date.getFullYear(), 4)}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Calendar arithmetic: the key `n` days after (or before, if negative) `key`. Throws on an invalid key. */
export function addDays(key: DateKey, n: number): DateKey {
	const utc = mustParse(key);
	const d = new Date(utc);
	return utcToKey(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + n));
}

/** Whole calendar days from `a` to `b` (positive when `b` is later). Throws on an invalid key. */
export function daysBetween(a: DateKey, b: DateKey): number {
	return Math.round((mustParse(b) - mustParse(a)) / DAY_MS);
}

/** 0 = Monday … 6 = Sunday. Throws on an invalid key. */
export function weekday(key: DateKey): number {
	return (new Date(mustParse(key)).getUTCDay() + 6) % 7;
}

const WEEKLY_CURVE: readonly Difficulty[] = [1, 2, 2, 3, 3, 4, 5];

/** The weekly curve: Mon 1, Tue 2, Wed 2, Thu 3, Fri 3, Sat 4, Sun 5. */
export function difficultyForDate(key: DateKey): Difficulty {
	return WEEKLY_CURVE[weekday(key)]!;
}

/** `ARROW #n`: 1 on `launchKey`, counting up one per calendar day. */
export function puzzleNumberForDate(key: DateKey, launchKey: DateKey): number {
	return daysBetween(launchKey, key) + 1;
}

// ---------------------------------------------------------------------------
// Player stats
// ---------------------------------------------------------------------------

/** One daily attempt: a (date, level) pair. */
export interface DayRecord {
	puzzleId: string;
	/** The `Arrow No. n` number (shared by the date's levels). */
	number: number;
	/** Epoch ms of the first letter. */
	startedAt: number;
	completedAt: number | null;
	/** The local day the player finished it. Only `completedOnDay === dateKey` counts for the streak. */
	completedOnDay: DateKey | null;
	elapsedMs: number;
	checksUsed: number;
	revealsUsed: number;
	bestFlow: number;
}

/** A date's attempts, one per level played. */
export type DayLevels = Partial<Record<Level, DayRecord>>;

/** Practice (Play) totals. Practice never touches the streak. */
export interface PracticeStats {
	/** Practice puzzles completed (a replay after a reset counts again). */
	completed: number;
	/** Sum of their solve times. */
	elapsedMs: number;
	checksUsed: number;
	revealsUsed: number;
}

/**
 * Everything the player's history knows, kept on the device.
 *
 * Version 2 holds one record per (date, level). Version 1 (one record per date, from
 * before levels) is read by `restoreStats`, its days becoming `medium`.
 */
export interface PlayerStats {
	version: 2;
	/** Keyed by the daily's date (the puzzle's date, not when it was played), then level. */
	days: Record<DateKey, DayLevels>;
	practice: PracticeStats;
}

export interface StartInfo {
	puzzleId: string;
	number: number;
	startedAt: number;
	/** Default `medium`. */
	level?: Level;
}

export interface CompletionResult {
	puzzleId: string;
	number: number;
	/** Default `medium`. */
	level?: Level;
	/** Used only if no start was recorded. Defaults to `completedAt`. */
	startedAt?: number;
	completedAt: number;
	elapsedMs: number;
	checksUsed: number;
	revealsUsed: number;
	bestFlow: number;
}

export interface PracticeResult {
	elapsedMs: number;
	checksUsed: number;
	revealsUsed: number;
}

const emptyPractice = (): PracticeStats => ({ completed: 0, elapsedMs: 0, checksUsed: 0, revealsUsed: 0 });

export function emptyStats(): PlayerStats {
	return { version: 2, days: {}, practice: emptyPractice() };
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isCount = (v: unknown): v is number => isNum(v) && Number.isInteger(v) && v >= 0;

function parseDay(raw: unknown): DayRecord | null {
	if (!isObj(raw)) return null;
	const { puzzleId, number, startedAt, completedAt, completedOnDay, elapsedMs, checksUsed, revealsUsed, bestFlow } =
		raw;
	if (typeof puzzleId !== 'string' || !isNum(number) || !isNum(startedAt)) return null;
	if (completedAt !== null && !isNum(completedAt)) return null;
	if (completedOnDay !== null && !isDateKey(completedOnDay)) return null;
	if ((completedAt === null) !== (completedOnDay === null)) return null;
	if (!isNum(elapsedMs) || elapsedMs < 0) return null;
	if (!isCount(checksUsed) || !isCount(revealsUsed) || !isCount(bestFlow)) return null;
	return { puzzleId, number, startedAt, completedAt, completedOnDay, elapsedMs, checksUsed, revealsUsed, bestFlow };
}

function parsePractice(raw: unknown): PracticeStats {
	if (!isObj(raw)) return emptyPractice();
	const { completed, elapsedMs, checksUsed, revealsUsed } = raw;
	return {
		completed: isCount(completed) ? completed : 0,
		elapsedMs: isNum(elapsedMs) && elapsedMs >= 0 ? elapsedMs : 0,
		checksUsed: isCount(checksUsed) ? checksUsed : 0,
		revealsUsed: isCount(revealsUsed) ? revealsUsed : 0,
	};
}

/** How many (date, level) records a raw stats blob holds, any version (for "did we drop any?"). */
export function countStatsRecords(raw: unknown): number {
	try {
		if (!isObj(raw) || !isObj(raw.days)) return 0;
		if (raw.version === 1) return Object.keys(raw.days).length;
		return Object.values(raw.days).reduce<number>((n, day) => n + (isObj(day) ? Object.keys(day).length : 1), 0);
	} catch {
		return 0;
	}
}

/** Every (date, level) record in `stats`. */
function records(stats: PlayerStats): Array<{ dateKey: DateKey; level: Level; day: DayRecord }> {
	const out: Array<{ dateKey: DateKey; level: Level; day: DayRecord }> = [];
	for (const [dateKey, levels] of Object.entries(stats.days)) {
		for (const level of LEVELS) {
			const day = levels[level];
			if (day) out.push({ dateKey, level, day });
		}
	}
	return out;
}

/** The number of (date, level) records in parsed stats. */
export function statsRecordCount(stats: PlayerStats): number {
	return records(stats).length;
}

/**
 * Defensive parse; never throws. Anything that isn't a version-1 or version-2 stats
 * object gives empty stats. Inside a valid object, a single malformed record is dropped
 * on its own rather than wiping every other day (and the streak).
 *
 * Version 1 (before levels: one record per date) migrates losslessly: each day becomes
 * that date's `medium` record, so streaks carry over unchanged.
 */
export function restoreStats(raw: unknown): PlayerStats {
	try {
		if (!isObj(raw) || !isObj(raw.days)) return emptyStats();
		const days: Record<DateKey, DayLevels> = {};
		if (raw.version === 1) {
			for (const [key, value] of Object.entries(raw.days)) {
				if (!isDateKey(key)) continue;
				const day = parseDay(value);
				if (day) days[key] = { [DEFAULT_LEVEL]: day };
			}
			return { version: 2, days, practice: emptyPractice() };
		}
		if (raw.version !== 2) return emptyStats();
		for (const [key, value] of Object.entries(raw.days)) {
			if (!isDateKey(key) || !isObj(value)) continue;
			const levels: DayLevels = {};
			for (const [level, record] of Object.entries(value)) {
				if (!isLevel(level)) continue;
				const day = parseDay(record);
				if (day) levels[level] = day;
			}
			if (Object.keys(levels).length > 0) days[key] = levels;
		}
		return { version: 2, days, practice: parsePractice(raw.practice) };
	} catch {
		return emptyStats();
	}
}

/**
 * Which of two records for the same (date, level) to keep when two devices' histories
 * meet: one that counts for the streak (completed on its own day) beats one that
 * doesn't, a completion beats an attempt, then the faster solve, then `a`.
 */
function betterRecord(dateKey: DateKey, a: DayRecord, b: DayRecord): DayRecord {
	const rank = (r: DayRecord) => (r.completedAt === null ? 0 : r.completedOnDay === dateKey ? 2 : 1);
	if (rank(a) !== rank(b)) return rank(a) > rank(b) ? a : b;
	if (a.completedAt !== null && b.completedAt !== null && a.elapsedMs !== b.elapsedMs) return a.elapsedMs < b.elapsedMs ? a : b;
	return a;
}

/**
 * Two stats histories as one (carrying a player's data to a new address, where they may
 * already have played): every (date, level) either side has, and where both have one,
 * the better record ({@link betterRecord}). A day counted for the streak on either side
 * still counts, so the streak is at least the longer of the two. Practice totals can't be
 * unioned, so the side with more practice solves wins whole. `a` wins ties throughout.
 */
export function mergeStats(a: PlayerStats, b: PlayerStats): PlayerStats {
	const days: Record<DateKey, DayLevels> = {};
	for (const key of new Set([...Object.keys(a.days), ...Object.keys(b.days)])) {
		const levels: DayLevels = {};
		for (const level of LEVELS) {
			const x = a.days[key]?.[level];
			const y = b.days[key]?.[level];
			const pick = x && y ? betterRecord(key, x, y) : (x ?? y);
			if (pick) levels[level] = { ...pick };
		}
		if (Object.keys(levels).length > 0) days[key] = levels;
	}
	const practice = b.practice.completed > a.practice.completed ? { ...b.practice } : { ...a.practice };
	return { version: 2, days, practice };
}

/**
 * Where the schedule starts: the date of No. 1 (or any known date and its number, as the
 * api's archive gives `earliest`). Numbers are one per calendar day from there.
 */
export interface ScheduleStart {
	date: DateKey;
	number: number;
}

/**
 * The stats as the schedule now stands: days before `start` dropped (they're not in the
 * archive and can't count, e.g. after the 2026-10-25 numbering reset deleted them) and
 * every kept record's `number` set from its date, so an old number never shows or is asked
 * about. With no (or a malformed) `start`, `stats` unchanged. Pure; never throws.
 */
export function alignStats(stats: PlayerStats, start: ScheduleStart | null | undefined): PlayerStats {
	if (!start || !isDateKey(start.date) || !Number.isInteger(start.number)) return stats;
	const days: Record<DateKey, DayLevels> = {};
	for (const [key, levels] of Object.entries(stats.days)) {
		if (!isDateKey(key) || key < start.date) continue;
		const number = start.number + daysBetween(start.date, key);
		const aligned: DayLevels = {};
		for (const level of LEVELS) {
			const day = levels[level];
			if (day) aligned[level] = day.number === number ? day : { ...day, number };
		}
		days[key] = aligned;
	}
	return { ...stats, days };
}

/** The days of `stats` dated before `start`: what {@link alignStats} hides. */
export function daysBefore(stats: PlayerStats, start: ScheduleStart | null | undefined): Record<DateKey, DayLevels> {
	if (!start || !isDateKey(start.date)) return {};
	return Object.fromEntries(Object.entries(stats.days).filter(([key]) => key < start.date));
}

/** The record for the daily at (`dateKey`, `level`), if any. */
export function dayRecord(stats: PlayerStats, dateKey: DateKey, level: Level = DEFAULT_LEVEL): DayRecord | null {
	return stats.days[dateKey]?.[level] ?? null;
}

function withDay(stats: PlayerStats, dateKey: DateKey, level: Level, day: DayRecord): PlayerStats {
	return { ...stats, days: { ...stats.days, [dateKey]: { ...stats.days[dateKey], [level]: day } } };
}

/**
 * Records that the daily for (`dateKey`, `info.level`) was started. An existing record
 * for the same puzzle is kept as-is (the first start wins); a record for a different
 * puzzle id is replaced. An invalid key or level leaves stats unchanged.
 */
export function recordStart(stats: PlayerStats, dateKey: DateKey, info: StartInfo): PlayerStats {
	const level = info.level ?? DEFAULT_LEVEL;
	if (!isDateKey(dateKey) || !isLevel(level)) return stats;
	const existing = dayRecord(stats, dateKey, level);
	if (existing && existing.puzzleId === info.puzzleId) return stats;
	return withDay(stats, dateKey, level, {
		puzzleId: info.puzzleId,
		number: info.number,
		startedAt: info.startedAt,
		completedAt: null,
		completedOnDay: null,
		elapsedMs: 0,
		checksUsed: 0,
		revealsUsed: 0,
		bestFlow: 0,
	});
}

/**
 * Records the completion of the daily for (`dateKey`, `result.level`), finished on local
 * day `completedOnDay` (pass today's key). The first completion is final: completing
 * the same puzzle again leaves the record unchanged, so a later replay can't turn a
 * same-day completion into an archive one. Invalid keys leave stats unchanged.
 */
export function recordCompletion(
	stats: PlayerStats,
	dateKey: DateKey,
	result: CompletionResult,
	completedOnDay: DateKey,
): PlayerStats {
	const level = result.level ?? DEFAULT_LEVEL;
	if (!isDateKey(dateKey) || !isDateKey(completedOnDay) || !isLevel(level)) return stats;
	const existing = dayRecord(stats, dateKey, level);
	if (existing && existing.puzzleId === result.puzzleId && existing.completedAt !== null) return stats;
	const startedAt =
		existing && existing.puzzleId === result.puzzleId ? existing.startedAt : (result.startedAt ?? result.completedAt);
	return withDay(stats, dateKey, level, {
		puzzleId: result.puzzleId,
		number: result.number,
		startedAt,
		completedAt: result.completedAt,
		completedOnDay,
		elapsedMs: result.elapsedMs,
		checksUsed: result.checksUsed,
		revealsUsed: result.revealsUsed,
		bestFlow: result.bestFlow,
	});
}

/**
 * Keeps the (date, level) record's `bestFlow` current mid-solve (before completion):
 * sets it to `max(recorded, bestFlow)`. Pure; returns `stats` unchanged for an invalid
 * key, a record that doesn't exist, a non-finite value, or a value that isn't a new best.
 */
export function recordBestFlow(
	stats: PlayerStats,
	dateKey: DateKey,
	bestFlow: number,
	level: Level = DEFAULT_LEVEL,
): PlayerStats {
	if (!isDateKey(dateKey) || !Number.isFinite(bestFlow)) return stats;
	const existing = dayRecord(stats, dateKey, level);
	const best = Math.floor(bestFlow);
	if (!existing || best <= existing.bestFlow) return stats;
	return withDay(stats, dateKey, level, { ...existing, bestFlow: best });
}

/** Counts one completed practice puzzle. Practice never touches the streak. */
export function recordPracticeCompletion(stats: PlayerStats, result: PracticeResult): PlayerStats {
	const p = stats.practice;
	const add = (v: number) => (Number.isFinite(v) && v > 0 ? v : 0);
	return {
		...stats,
		practice: {
			completed: p.completed + 1,
			elapsedMs: p.elapsedMs + add(result.elapsedMs),
			checksUsed: p.checksUsed + Math.floor(add(result.checksUsed)),
			revealsUsed: p.revealsUsed + Math.floor(add(result.revealsUsed)),
		},
	};
}

/** The levels of `dateKey`'s daily the player has completed (any day), easiest first. */
export function completedLevels(stats: PlayerStats, dateKey: DateKey): Level[] {
	const day = stats.days[dateKey];
	return day ? LEVELS.filter((level) => day[level]?.completedAt != null) : [];
}

/**
 * A day counts for the streak if any of its levels was completed on that same local day.
 */
function counts(stats: PlayerStats, key: DateKey): boolean {
	const day = stats.days[key];
	return day !== undefined && LEVELS.some((level) => day[level]?.completedAt != null && day[level]?.completedOnDay === key);
}

/**
 * Consecutive counted days ending today — or ending yesterday if today isn't done yet,
 * so the streak doesn't look broken before the player has played. A day counts when
 * any level was completed on its own day; archive plays never count. Returns 0 for an
 * invalid key.
 */
export function currentStreak(stats: PlayerStats, todayKey: DateKey): number {
	if (!isDateKey(todayKey)) return 0;
	let key = counts(stats, todayKey) ? todayKey : addDays(todayKey, -1);
	let streak = 0;
	while (counts(stats, key)) {
		streak++;
		key = addDays(key, -1);
	}
	return streak;
}

/** The longest run of consecutive counted days ever. */
export function bestStreak(stats: PlayerStats): number {
	const keys = Object.keys(stats.days)
		.filter((k) => isDateKey(k) && counts(stats, k))
		.sort();
	let best = 0;
	let run = 0;
	let prev: DateKey | null = null;
	for (const key of keys) {
		run = prev !== null && daysBetween(prev, key) === 1 ? run + 1 : 1;
		best = Math.max(best, run);
		prev = key;
	}
	return best;
}

export interface Comparison {
	/** null unless both today's and yesterday's dailies (at this level) are completed. */
	fasterThanYesterday: boolean | null;
	/** today − yesterday in ms (negative = faster), or null. */
	deltaMs: number | null;
}

/**
 * For the result card ("Faster than yesterday"), at one level (default `medium`): times
 * are only comparable within a level. Any completion counts here, archive included.
 */
export function comparison(stats: PlayerStats, todayKey: DateKey, level: Level = DEFAULT_LEVEL): Comparison {
	if (!isDateKey(todayKey)) return { fasterThanYesterday: null, deltaMs: null };
	const today = dayRecord(stats, todayKey, level);
	const yesterday = dayRecord(stats, addDays(todayKey, -1), level);
	if (!today || !yesterday || today.completedAt === null || yesterday.completedAt === null) {
		return { fasterThanYesterday: null, deltaMs: null };
	}
	const deltaMs = today.elapsedMs - yesterday.elapsedMs;
	return { fasterThanYesterday: deltaMs < 0, deltaMs };
}

// ---------------------------------------------------------------------------
// The Stats screen
// ---------------------------------------------------------------------------

export interface SolveTotals {
	/** Completed dailies (archive plays included). */
	completed: number;
	/** Mean solve time over the completions finished on their own day (rounded ms), or null with none. */
	averageMs: number | null;
	/**
	 * Fastest solve on its own day with no reveals (a revealed answer isn't a solve; an
	 * archive catch-up isn't a race), or null with none. Checks are allowed.
	 */
	bestMs: number | null;
	checksUsed: number;
	revealsUsed: number;
}

export interface StatsSummary {
	currentStreak: number;
	bestStreak: number;
	/** Every completed daily, any level. */
	dailies: SolveTotals;
	byLevel: Record<Level, SolveTotals>;
	/** Distinct dates with at least one level completed (archive plays included). */
	daysCompleted: number;
	practiceCompleted: number;
	/** Dailies + practice puzzles completed. */
	totalCompleted: number;
	/** Checks and reveals over every completed puzzle, practice included. */
	checksUsed: number;
	revealsUsed: number;
}

/**
 * Totals over a set of (date, level) records. Every completion counts toward `completed`
 * and the checks/reveals; the times (`averageMs`, `bestMs`) only from solves finished on
 * their own day, so catching up an old puzzle at leisure never sets a best or skews the
 * average.
 */
function totals(recs: ReadonlyArray<{ dateKey: DateKey; day: DayRecord }>): SolveTotals {
	const done = recs.filter((r) => r.day.completedAt !== null);
	const onTime = done.filter((r) => r.day.completedOnDay === r.dateKey).map((r) => r.day);
	const clean = onTime.filter((d) => d.revealsUsed === 0);
	const sum = onTime.reduce((n, d) => n + d.elapsedMs, 0);
	return {
		completed: done.length,
		averageMs: onTime.length ? Math.round(sum / onTime.length) : null,
		bestMs: clean.length ? Math.min(...clean.map((d) => d.elapsedMs)) : null,
		checksUsed: done.reduce((n, r) => n + r.day.checksUsed, 0),
		revealsUsed: done.reduce((n, r) => n + r.day.revealsUsed, 0),
	};
}

/** Everything the Stats screen shows, from the stats and today's local date. Pure. */
export function summarizeStats(stats: PlayerStats, todayKey: DateKey): StatsSummary {
	const all = records(stats);
	const dailies = totals(all);
	const byLevel = Object.fromEntries(
		LEVELS.map((level) => [level, totals(all.filter((r) => r.level === level))]),
	) as Record<Level, SolveTotals>;
	const daysCompleted = Object.keys(stats.days).filter((key) => completedLevels(stats, key).length > 0).length;
	return {
		currentStreak: currentStreak(stats, todayKey),
		bestStreak: bestStreak(stats),
		dailies,
		byLevel,
		daysCompleted,
		practiceCompleted: stats.practice.completed,
		totalCompleted: dailies.completed + stats.practice.completed,
		checksUsed: dailies.checksUsed + stats.practice.checksUsed,
		revealsUsed: dailies.revealsUsed + stats.practice.revealsUsed,
	};
}
